import { api, Banner, LineChart, Metric, PageHead, useAsync } from "@getaways/extranet-ui";
import { FilterBar, useFilters, useOptions } from "../lib/filters";
import { int } from "../lib/format";
import { filterOf, ResultTable, useQuery } from "../lib/query";

const pct = (a: number | null | undefined, b: number | null | undefined) => (a === null || a === undefined || !b ? "—" : `${((a / b) * 100).toFixed(1)}%`);

/** From visit to sale (docs/36 §7): GA4 where its BigQuery export is linked, then CRM requests, quotes, orders, sales. */
export function FunnelPage() {
  const [filters, setFilters] = useFilters("funnel");
  const options = useOptions();
  const status = useAsync(() => api<{ ga4: boolean }>("GET", "/v1/status"), []);
  const f = filterOf(filters);
  const measures = ["sessions", "item_views", "checkouts", "requests", "quotes", "funnel_orders", "funnel_sales", "funnel_sell"];
  const totals = useQuery({ table: "funnel", measures, filter: f });
  const daily = useQuery({ table: "funnel", measures: ["sessions", "quotes", "funnel_sales"], dimensions: ["day"], filter: f, sort: "day", dir: "asc", limit: 400 });
  const t = totals.data?.totals;
  const rows = daily.data?.rows ?? [];
  return (
    <>
      <PageHead title="Funnel" sub="From a visit on the brand sites to a sale, per brand and day." />
      {status.data && !status.data.ga4 && <Banner notice={{ tone: "warn", text: "GA4's BigQuery export is not linked yet: sessions, views and checkouts stay empty. The platform's half (requests, quotes, orders, sales) is shown." }} />}
      <div className="card" style={{ padding: 0, marginBottom: 14 }}>
        <FilterBar filters={filters} onChange={setFilters} options={options} supplierFilter={false} statusFilter={false} />
      </div>
      <div className="metrics">
        <Metric label="Sessions" value={int(t?.sessions)} />
        <Metric label="Checkouts begun" value={int(t?.checkouts)} hint={pct(t?.checkouts, t?.sessions)} />
        <Metric label="Requests (CRM)" value={int(t?.requests)} />
        <Metric label="Quotes" value={int(t?.quotes)} />
        <Metric label="Orders" value={int(t?.funnel_orders)} hint={`${pct(t?.funnel_orders, t?.quotes)} of quotes`} />
        <Metric label="Sales" value={int(t?.funnel_sales)} hint={`${pct(t?.funnel_sales, t?.quotes)} of quotes`} />
      </div>
      {rows.length > 1 && (
        <section className="card" style={{ marginBottom: 14 }}>
          <h2 style={{ marginTop: 0, fontSize: 15 }}>Per day</h2>
          <LineChart label="Sessions, quotes and sales per day" categories={rows.map((r) => String(r.day).slice(5))} categoryTitle={(i) => String(rows[i]!.day)} format={(v) => int(v)} height={200} series={[
            { label: "Quotes", color: "var(--series-1)", values: rows.map((r) => Number(r.quotes ?? 0)) },
            { label: "Sales", color: "var(--series-2)", values: rows.map((r) => Number(r.funnel_sales ?? 0)) },
            ...(status.data?.ga4 ? [{ label: "Sessions", color: "var(--series-3)", values: rows.map((r) => (r.sessions === null ? null : Number(r.sessions))) }] : []),
          ]} />
        </section>
      )}
      <ResultTable title="By brand" spec={{ table: "funnel", measures: ["sessions", "checkouts", "requests", "quotes", "funnel_orders", "funnel_sales", "quote_conversion", "conversion"], dimensions: ["brand"], filter: f }} />
    </>
  );
}
